import {
  Button,
  Badge,
  ScrollArea,
  Label,
  Input,
  Textarea,
  Alert, AlertDescription
} from '@creanizer/ui'
import { Trash2, Copy, FileText, Edit, Eye, ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import type { SavedImagePrompt } from '@creanizer/types'
import { ExportImportButtons } from '../../../../components/common'

interface SavedPromptsListProps {
  savedPrompts: SavedImagePrompt[]
  onLoad: (prompt: SavedImagePrompt) => void
  onUpdate: (id: string, updates: Partial<SavedImagePrompt>) => Promise<void>
  onDelete: (id: string) => Promise<void>
  onDeleteAll: () => Promise<void>
  onExportText: () => void
  onExportJSON: () => void
  onImportJSON: (jsonString: string) => Promise<void>
}

export function SavedPromptsList({
  savedPrompts,
  onLoad,
  onUpdate,
  onDelete,
  onDeleteAll,
  onExportText,
  onExportJSON,
  onImportJSON
}: SavedPromptsListProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editMemo, setEditMemo] = useState('')
  const [importError, setImportError] = useState<string | null>(null)
  
  const handleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id)
  }
  
  const handleEdit = (prompt: SavedImagePrompt) => {
    setEditingId(prompt.id)
    setEditTitle(prompt.title)
    setEditMemo(prompt.memo || '')
  }
  
  const handleSaveEdit = async (id: string) => {
    await onUpdate(id, {
      title: editTitle,
      memo: editMemo
    })
    setEditingId(null)
  }
  
  const handleCancelEdit = () => {
    setEditingId(null)
    setEditTitle('')
    setEditMemo('')
  }
  
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
  }
  
  // Import handled by ExportImportButtons component
  
  const getModelDisplayName = (model: string) => {
    const modelNames: Record<string, string> = {
      'novelai_v45': 'NovelAI V4.5',
      'midjourney_v7': 'Midjourney V7',
      'sdxl_standard': 'Stable Diffusion XL',
      'pony_v6': 'Pony V6 XL',
      'animagine_v4': 'Animagine XL 4.0',
      'illustrious': 'Illustrious XL',
      'noobai_xl': 'NoobAI XL'
    }
    return modelNames[model] || model
  }
  
  const getImageTypeDisplayName = (type: string) => {
    const typeNames: Record<string, string> = {
      'portrait': '立ち絵',
      'illustration': '一枚絵・シーン',
      'background': '背景'
    }
    return typeNames[type] || type
  }
  
  if (savedPrompts.length === 0) {
    return (
      <div className="card">
        <div className="py-8">
          <div className="text-center space-y-4">
            <p className="text-muted-foreground">
              保存済みのプロンプトはありません
            </p>
            <div className="flex justify-center">
              <ExportImportButtons
                onExportText={onExportText}
                onExportJSON={onExportJSON}
                onImportJSON={onImportJSON}
                dataTypeName="画像プロンプト"
                disabled={false}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <>
      <div className="card">
        <div className="p-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">
              保存済みプロンプト ({savedPrompts.length}/1000)
            </h3>
            <div className="flex gap-2">
              <ExportImportButtons
                onExportText={onExportText}
                onExportJSON={onExportJSON}
                onImportJSON={onImportJSON}
                dataTypeName="画像プロンプト"
              />
              <Button
                size="sm"
                variant="destructive"
                onClick={onDeleteAll}
                disabled={savedPrompts.length === 0}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                全削除
              </Button>
            </div>
          </div>
        </div>
        <div className="px-6 pb-6">
          {importError && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{importError}</AlertDescription>
            </Alert>
          )}
          
          <ScrollArea className="h-[600px]">
            <div className="space-y-2">
              {savedPrompts.map((prompt) => (
                <div
                  key={prompt.id}
                  className="border rounded-lg overflow-hidden"
                >
                  {/* プロンプトヘッダー */}
                  <div className="p-3 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        {editingId === prompt.id ? (
                          <div className="space-y-2">
                            <Input
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              placeholder="タイトル"
                            />
                            <Textarea
                              value={editMemo}
                              onChange={(e) => setEditMemo(e.target.value)}
                              placeholder="メモ（任意）"
                              rows={2}
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleSaveEdit(prompt.id)}
                              >
                                保存
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancelEdit}
                              >
                                キャンセル
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="font-medium">{prompt.title}</div>
                            <div className="flex gap-2 mt-1">
                              <Badge variant="secondary">
                                {getModelDisplayName(prompt.input.targetModel)}
                              </Badge>
                              <Badge variant="outline">
                                {getImageTypeDisplayName(prompt.input.imageType)}
                              </Badge>
                              {prompt.input.useProMode && (
                                <Badge variant="default">Pro</Badge>
                              )}
                            </div>
                            {prompt.memo && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {prompt.memo}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              作成: {new Date(prompt.createdAt).toLocaleDateString('ja-JP')}
                            </p>
                          </>
                        )}
                      </div>
                      {editingId !== prompt.id && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleExpand(prompt.id)}
                          >
                            {expandedId === prompt.id ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onLoad(prompt)}
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(prompt)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onDelete(prompt.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* 展開された詳細 */}
                  {expandedId === prompt.id && (
                    <div className="border-t p-3 bg-gray-50 space-y-3">
                      <div className="space-y-2">
                        <Label>ポジティブプロンプト</Label>
                        <div className="relative">
                          <Textarea
                            value={prompt.result.positivePrompt}
                            readOnly
                            className="min-h-[100px] font-mono text-sm pr-10"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            className="absolute top-2 right-2"
                            onClick={() => handleCopy(prompt.result.positivePrompt)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        {prompt.result.translatedPositivePrompt && (
                          <div className="p-3 bg-blue-50 rounded border border-blue-200">
                            <Label className="text-blue-900 mb-2 block">日本語翻訳</Label>
                            <p className="text-sm text-blue-800 whitespace-pre-wrap">
                              {prompt.result.translatedPositivePrompt}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>ネガティブプロンプト</Label>
                        <div className="relative">
                          <Textarea
                            value={prompt.result.negativePrompt}
                            readOnly
                            className="min-h-[60px] font-mono text-sm pr-10"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            className="absolute top-2 right-2"
                            onClick={() => handleCopy(prompt.result.negativePrompt)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        {prompt.result.translatedNegativePrompt && (
                          <div className="p-3 bg-blue-50 rounded border border-blue-200">
                            <Label className="text-blue-900 mb-2 block">日本語翻訳</Label>
                            <p className="text-sm text-blue-800 whitespace-pre-wrap">
                              {prompt.result.translatedNegativePrompt}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <Label>設定</Label>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="font-medium">画像タイプ:</span>{' '}
                            {getImageTypeDisplayName(prompt.input.imageType)}
                          </div>
                          <div>
                            <span className="font-medium">モデル:</span>{' '}
                            {getModelDisplayName(prompt.input.targetModel)}
                          </div>
                          <div>
                            <span className="font-medium">創造性レベル:</span>{' '}
                            {prompt.input.creativityLevel}
                          </div>
                          <div>
                            <span className="font-medium">Proモード:</span>{' '}
                            {prompt.input.useProMode ? 'ON' : 'OFF'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>
    </>
  )
}